import { providerConversationText } from "../../supabase/functions/_shared/providerConversationText";

const PROVIDER_URL_PATTERN = /\bhttps?:\/\/[^\s<>"'`]+/gi;
const PREVIEW_TOKEN_PATTERN = /_preview_token=[^\s&"')<>]+/gi;
const PREVIEW_HOST_PATTERN = /\bpreview-sandbox--[a-z0-9-]+(?:\.[a-z0-9.-]+)?/gi;
const PROVIDER_HOST_PATTERN = /\b(?:[a-z0-9-]+\.)?(?:base44|provider)\.app\b/gi;
const PROVIDER_EDITOR_HOST_PATTERN = /\b(?:[a-z0-9-]+\.)?app\.base44\.com\b/gi;
const PROVIDER_APP_PATH_PATTERN = /\bapps\/[a-z0-9]{12,}\b/gi;
const IP_ADDRESS_PATTERN = /\b\d{1,3}(?:\.\d{1,3}){3}(?::\d{2,5})?\b/g;

function isSensitiveProviderUrl(value: string): boolean {
  try {
    const url = new URL(value);
    const host = url.hostname.toLowerCase();
    return url.searchParams.has("_preview_token") ||
      host === "base44.app" ||
      host.endsWith(".base44.app") ||
      host === "base44-preview.app" ||
      host.endsWith(".base44-preview.app") ||
      host === "app.base44.com" ||
      host.endsWith(".app.base44.com") ||
      host === "provider.app" ||
      host.endsWith(".provider.app") ||
      host.includes("preview-sandbox--");
  } catch {
    return /_preview_token=|preview-sandbox--|\.base44\.app|base44-preview\.app|app\.base44\.com|\.provider\.app/i.test(value);
  }
}

function hasSensitiveProviderReference(value: string): boolean {
  return /_preview_token=|preview-sandbox--|\.base44\.app|base44-preview\.app|app\.base44\.com|\.provider\.app/i.test(value);
}

function isLowLevelProviderNetworkError(value: string): boolean {
  const lower = value.toLowerCase();
  return hasSensitiveProviderReference(value) &&
    (
      lower.includes("error sending request") ||
      lower.includes("sendrequest") ||
      lower.includes("connection reset") ||
      lower.includes("connection refused") ||
      lower.includes("econnreset") ||
      lower.includes("client error")
    );
}

export function sanitizeProviderPublicText(value?: string | null): string | null {
  const original = value?.trim();
  if (!original) return null;

  const compact = original.replace(/\s+/g, " ").trim();
  const lower = compact.toLowerCase();

  if (isLowLevelProviderNetworkError(compact)) {
    return "Provider preview connection was interrupted.";
  }

  if (hasSensitiveProviderReference(compact) && lower.includes("the signal has been aborted")) {
    return "Provider preview request timed out.";
  }

  return compact
    .replace(PROVIDER_URL_PATTERN, (url) => isSensitiveProviderUrl(url) ? "Provider preview URL" : url)
    .replace(PREVIEW_TOKEN_PATTERN, "preview token")
    .replace(PROVIDER_EDITOR_HOST_PATTERN, "Provider editor host")
    .replace(PREVIEW_HOST_PATTERN, "Provider preview host")
    .replace(PROVIDER_HOST_PATTERN, "Provider preview host")
    .replace(PROVIDER_APP_PATH_PATTERN, "apps/<provider-app>")
    .replace(IP_ADDRESS_PATTERN, "network endpoint")
    .replace(/\bBase44\b/g, "Provider")
    .replace(/\bbase44\b/g, "Provider")
    .replace(/\s+([.,;:!?])/g, "$1")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Applies the same provider redaction rules while retaining Markdown line
 * boundaries and bounded indentation for user-visible assistant messages.
 */
export function sanitizeProviderPublicMarkdown(value?: string | null): string | null {
  return providerConversationText(value);
}
